import type { QueryClient } from "@tanstack/react-query";
import type { RouterHistory } from "@tanstack/react-router";
import {
  Navigate,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { bareHandle } from "./lib/api";
import { saveKnockTarget } from "./lib/draft";
import { meQuery } from "./lib/queries";
import { RootLayout } from "./routes/__root";
import { HomeRoute } from "./routes/index";
import { KnockRoute } from "./routes/knock";
import { YouPlanRoute } from "./routes/you-plan";

/**
 * The app's addresses — four of them, where there were eleven.
 *
 * WHAT AN ADDRESS IS FOR, now that it has been decided. A URL is for a place
 * somebody arrives at from OUTSIDE: a link in a bio, a bookmark, a redirect
 * back from a payment processor. It is not for a place somebody moves to from
 * inside the product — that is a pane, and giving it a URL only means the Back
 * button can put the interface into a state nobody navigated to.
 *
 * Under that rule the rooms all fail: `/inbox`, `/knocks`, `/knocks/:id`,
 * `/rules`, `/you` were places you went from a rail, and the
 * rail is gone. They are deleted outright — no redirects, no aliases (the
 * repo's no-backward-compatibility rule; there are no links in the world to
 * break).
 *
 * The three that survive each pass the test:
 *   `/`             the product, and the landing when signed out
 *   `/@handle`      somebody's door — the one address the product hands out
 *   `/you/plan`     where a subscription checkout returns, and the address the
 *                   auto-renewal disclosure names by law (docs/plans.md §9)
 *
 * THERE WERE FIVE, and two went in one day. `/board` was the demand board's own
 * room, and ruling R-0916-3 deleted it: a ranked list of the handles most people
 * are waiting at is exactly the "ordered list of doors or of people" ToU 2.2
 * §5B.2 says this product publishes in no form. `/wallet` was where a credits
 * checkout came back, and ruling R-0916-1 deleted the credits. The last address
 * standing mounts the shell with a panel open; it is an address because a
 * processor's return URL has to be one, and for no other reason.
 *
 * Still code-based rather than file-based, and still no code splitting: FR-1.2's
 * single mount is the product's feel, and every screen being already in the
 * bundle is what pays for it.
 */

const HANDLE_RE = /^[a-z0-9_]{1,15}$/;

/**
 * What the browser is carrying back from checkout, once it is in an address of
 * ours rather than Stripe's. Spelled as words: the router's search serialiser
 * JSON-quotes a value that would otherwise read back as a number, and `"1"` in
 * the URL is not the `1` the flag means.
 */
type Paid = "paid" | "unpaid";

function paidOf(value: unknown): Paid | undefined {
  return value === "paid" ? "paid" : value === "unpaid" ? "unpaid" : undefined;
}

/**
 * `?poster=1` — see your own door as a stranger does (owner item 14).
 *
 * Both spellings are admitted because both really occur: a hand-typed or
 * hand-written `poster=1` parses as the NUMBER 1 (the router runs values through
 * `JSON.parse`), while a link the router itself serialises round-trips as the
 * string. One flag, two ways of writing it down, and neither is a different
 * feature.
 */
function posterOf(value: unknown): 1 | undefined {
  return value === 1 || value === "1" ? 1 : undefined;
}

/**
 * Search validators are hand-written rather than zod schemas: `@nob/web` does
 * not depend on zod (Bun's isolated installs scope it to the api and the shared
 * schemas package), and three enums of two members do not justify pulling a
 * validator library into a bundle whose size is a product decision.
 */

type HomeSearch = {
  knock?: string | undefined;
  paid?: Paid | undefined;
  poster?: 1 | undefined;
};
type KnockSearch = { poster?: 1 | undefined };
type PaidSearch = { paid?: Paid | undefined };
/**
 * `/you/plan` also takes `?portal=1` (wave 87): the processor's billing portal
 * comes back here, and the panel reopens on its index — where the card and the
 * billing details are — rather than on the ladder.
 */
type YouPlanSearch = PaidSearch & { portal?: 1 | undefined };

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  // Every path lands somewhere; there is no dead screen in this product. An
  // address this session cannot answer is the front room, said in one hop —
  // which now includes every room this wave deleted.
  notFoundComponent: () => <Navigate to="/" search={{}} replace />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (search: HomeSearch): HomeSearch => ({
    ...(typeof search.knock === "string" && search.knock ? { knock: search.knock } : {}),
    ...(paidOf(search.paid) ? { paid: paidOf(search.paid) } : {}),
    ...(posterOf(search.poster) ? { poster: posterOf(search.poster) } : {}),
  }),
  /**
   * `?knock=<handle>` is how the web app is ENTERED at a door. On one origin
   * `/@handle` is the api's server-rendered door page (ruling R-0926-1 (1)), so
   * a hard load of it never reaches this bundle: the page's "sign in" hands a
   * stranger here, and a visitor with a session is sent here directly, with
   * `?poster=1` when that is what they asked for. It is read once, stored —
   * sign-in and checkout both leave the origin and come back to a bare `/` —
   * and swapped for the web app's own `/@handle`, client-side.
   *
   * Poster mode stores nothing here: whether the flag is the owner's is the
   * door route's question (it awaits the session), and it stores the target
   * for everybody else.
   */
  beforeLoad: ({ search, context }) => {
    if (search.knock !== undefined) {
      const handle = bareHandle(search.knock).toLowerCase();
      if (HANDLE_RE.test(handle)) {
        if (search.poster === undefined) saveKnockTarget(handle);
        throw redirect({
          to: "/@{$handle}",
          params: { handle },
          search: search.poster === undefined ? {} : { poster: search.poster },
          replace: true,
        });
      }
      // Not a handle X would allow, so not a door — and not something to leave
      // in the address bar either.
      throw redirect({ to: "/", search: {}, replace: true });
    }
    // Warmed here so the shell and this screen's first render agree about who
    // is signed in. A failure is the screen's to say, not the router's.
    return context.queryClient.ensureQueryData(meQuery()).then(
      () => undefined,
      () => undefined,
    );
  },
  component: HomeRoute,
});

const knockRoute = createRoute({
  getParentRoute: () => rootRoute,
  // The `@` is a literal prefix on the segment, not part of the handle: the
  // address reads the way the product writes it, and `params.handle` is bare.
  path: "/@{$handle}",
  params: {
    // A handle is X's, so its rules are X's. Anything else is not a door, and
    // returning `false` means this route simply does not match — which lands
    // on the not-found above, which is home.
    parse: ({ handle }: { handle: string }) => {
      const bare = handle.toLowerCase();
      return HANDLE_RE.test(bare) ? { handle: bare } : false;
    },
  },
  // Only the poster flag. A checkout never returns to a door — Stripe comes
  // back to `/` or `/you/plan` — so `?paid=` was a flag this address carried
  // and nothing at it ever read (ruling R-0827-1 took the intake away).
  validateSearch: (search: KnockSearch): KnockSearch =>
    posterOf(search.poster) ? { poster: posterOf(search.poster) } : {},
  /**
   * The door this visit is for, stored before anything else happens. Both roads
   * out of the app — the identity provider and Stripe — come back to a bare
   * `/`, and losing the door on the way back would send the sender to the one
   * screen they did not ask for.
   *
   * EXCEPT IN POSTER MODE, which is an owner looking at their own plate rather
   * than a visit to knock at it (item 14). Aiming the next return trip at this
   * door would send them back to somebody's door — their own — instead of to the
   * console they came from.
   *
   * AND POSTER MODE IS THE OWNER'S ALONE (ruling R-0914-2 (2)), so the session
   * has to have answered before the flag means anything: a stranger who typed
   * `?poster=1` is making an ordinary visit to somebody's door and must keep
   * the target, or signing in would land them back at a bare `/` with nothing
   * to aim at. That is why the `me` read is AWAITED here rather than warmed and
   * left — the decision this line makes depends on its answer.
   */
  beforeLoad: async ({ params, search, context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery()).catch(() => null);
    const owner = me?.xHandle?.toLowerCase() === params.handle;
    if (search.poster === undefined || !owner) saveKnockTarget(params.handle);
  },
  component: KnockRoute,
});

/** Signed out there is no shell to open, so go home, where signing in is. */
async function requireSession(queryClient: QueryClient): Promise<void> {
  const me = await queryClient.ensureQueryData(meQuery()).catch(() => null);
  if (!me) throw redirect({ to: "/", search: {} });
}

// `/wallet` stood here, and was where a credits checkout returned. Ruling
// R-0916-1 deleted the checkout and the room it returned to; the address goes
// with them rather than redirecting, because there are no links in the world to
// break and a redirect is a promise to keep one working.

const youPlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/you/plan",
  validateSearch: (search: YouPlanSearch): YouPlanSearch => ({
    ...(paidOf(search.paid) ? { paid: paidOf(search.paid) } : {}),
    ...(String(search.portal) === "1" ? { portal: 1 as const } : {}),
  }),
  beforeLoad: ({ context }) => requireSession(context.queryClient),
  component: YouPlanRoute,
});

/**
 * `/board` is gone (ruling R-0916-3). It was the demand board's own room — a
 * ranked list of handles nobody had opened a door at, each with its count — and
 * ToU 2.2 §5B.2 publishes "no ranking, league table or ordered list of doors or
 * of people, in any form". No redirect: nothing is live, so there is no link to
 * keep working.
 */
export const routeTree = rootRoute.addChildren([indexRoute, knockRoute, youPlanRoute]);

/** `history` is only ever passed by the test tier, which uses an in-memory one. */
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  const router = createRouter({
    routeTree,
    context: { queryClient },
    ...(history ? { history } : {}),
    scrollRestoration: true,
    defaultPreload: false,
  });
  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
